import React from 'react';
import PropTypes from 'prop-types';
import parse, { domToReact } from 'html-react-parser';

import styled from 'styled-components';
import { get } from 'lodash';

import { Show } from '../show';
import { exerciseTypes } from 'z-js-commons';

import { CORRECT, WRONG, MISSING, WAITING_FOR_CORRECTION, INCOMPLETE } from '../../lib/exercise';

import Accordion from '../accordion';
import { Body } from '../typography';

import BarrageFeedback from './barrage';
import DragDropFeedback from './drag-drop';
import FillBlanksFeedback from './fill-blanks';
import FillChoiceFeedback from './fill-choice';
import FindErrorFeedback from './find-error';
import MultipleChoiceFeedback from './multiple-choice';
import SingleChoiceFeedback from './single-choice';
import TrueFalseFeedback from './true-false';
import OpenAnswerFeedback from './open-answer';
import FeedbackNotImplemented from './not-implemented';
import MathContent from '../math-content';
import CheckerFeedback from './checker';
import Highlighter from '../highlither';
import { parseMfenced } from '../../lib/utils';

export const GenericFeedback = styled(MathContent)`
    font-size: 16px;
    margin: 0 32px 24px 32px;
    padding-bottom: 24px;

    ${(props) => props.theme.mediaQueries.desktop} {
        margin-bottom: 32px;
        padding-bottom: 32px;
    }

    border-bottom: 1px solid var(--gray700);

    img {
        margin: 4px;
        vertical-align: middle;
    }
`;

export const TreatmentContainer = styled.div`
    padding: 0 32px 24px 32px;

    & > div {
        margin-top: 8px;
    }

    img {
        display: inline-block;
        max-width: 100%;
        width: auto !important; /* per sovrascrivere le dimensioni impostate su CAT alle immagini dei treatment */
        height: auto !important; /* per sovrascrivere le dimensioni impostate su CAT alle immagini dei treatment */
    }
`;

const MathScroll = styled.div`
    max-width: 100%;
    overflow-x: auto;
`;

const selectFeedbackComponent = (exercise) => {
    const exerciseType = exerciseTypes.getExerciseTypeFromName(exercise.typeLabel);

    switch (exerciseType) {
        case exerciseTypes.MULTIPLE_CHOICE:
            const correctAnswers = exercise.options.filter((x) => x.correct);
            return correctAnswers.length === 1 ? SingleChoiceFeedback : MultipleChoiceFeedback;
        case exerciseTypes.TRUE_FALSE:
            return TrueFalseFeedback;
        case exerciseTypes.FILL_CHOICE:
            return FillChoiceFeedback;
        case exerciseTypes.SHOTS:
            return BarrageFeedback;
        case exerciseTypes.DRAG_DROP:
            return DragDropFeedback;
        case exerciseTypes.FILL_BLANKS:
            return FillBlanksFeedback;
        case exerciseTypes.FIND_ERROR:
            return FindErrorFeedback;
        case exerciseTypes.OPEN_ANSWER:
            return OpenAnswerFeedback;
        case exerciseTypes.CHECKER:
            return CheckerFeedback;
        default:
            return FeedbackNotImplemented;
    }
};

const Feedback = ({ answers = [], exercise, correctionStatus }) => {
    const FeedbackComponent = selectFeedbackComponent(exercise);

    const getColorAccordingStatus = () => {
        if (!correctionStatus) {
            return 'surface-grey-700';
        }

        switch (correctionStatus.state) {
            case CORRECT:
                return 'success-green-main';
            case INCOMPLETE:
                return 'error-red-main';
            case WRONG:
                return 'error-red-main';
            case MISSING:
                return 'surface-grey-700';
            case WAITING_FOR_CORRECTION:
                return 'warning-yellow-main';
            default:
                return 'surface-grey-700';
        }
    };

    const getTitle = () => {
        if (!correctionStatus) {
            return 'Esercizio non svolto';
        }

        switch (correctionStatus.state) {
            case CORRECT:
                return 'Esercizio esatto';
            case WRONG:
                return 'Esercizio sbagliato';
            case WAITING_FOR_CORRECTION:
                return 'Esercizio da correggere con l’insegnante';
            case MISSING:
                return 'Esercizio non svolto';
            default:
                return 'Esercizio sbagliato';
        }
    };

    const genericFeedback = get(exercise, 'feedbacks.genericFeedback', '');
    const genericCorrectFeedback = get(exercise, 'feedbacks.genericCorrectFeedback', '');
    const genericErrorFeedback = get(exercise, 'feedbacks.genericErrorFeedback', '');

    const options = {
        replace: (domNode) => {
            if (domNode.name && domNode.name === 'mfenced') {
                return parseMfenced(domNode, options);
            }
        },
    };

    return (
        <Accordion sideFlagColor={getColorAccordingStatus()} defaultOpened={true}>
            <Body lv={3} fontWeight="semi-bold">
                {getTitle()}
            </Body>
            <div>
                <Show
                    when={
                        genericFeedback ||
                        (correctionStatus && correctionStatus.state === CORRECT && genericCorrectFeedback) ||
                        (correctionStatus && correctionStatus.state === WRONG && genericErrorFeedback)
                    }
                >
                    <GenericFeedback>
                        <div>{parse(genericFeedback, options)}</div>
                        <span>
                            {parse(
                                correctionStatus && correctionStatus.state === CORRECT ? genericCorrectFeedback : genericErrorFeedback,
                                options,
                            )}
                        </span>
                    </GenericFeedback>
                </Show>
                <FeedbackComponent answers={answers} exercise={exercise} correctionStatus={correctionStatus} />
                {exercise.treatment.length > 0 && (
                    <TreatmentContainer>
                        <Body level={3} fontWeight="semi-bold">
                            {'Svolgimento'}
                        </Body>
                        {exercise.treatment.map((item, index) => {
                            return (
                                <Highlighter key={index}>
                                    <MathScroll>
                                        <MathContent>{item.text}</MathContent>
                                    </MathScroll>
                                </Highlighter>
                            );
                        })}
                    </TreatmentContainer>
                )}
            </div>
        </Accordion>
    );
};

Feedback.propTypes = {
    answers: PropTypes.array,
    exercise: PropTypes.object,
    correctionStatus: PropTypes.object,
};

export default Feedback;
